import React, { useEffect, useRef, useState } from 'react';
import type {
  GbpNextPost,
  GbpOfferSync,
  GbpPhotoFreshness,
  GbpPost,
  GbpProfileItem,
  GbpQa,
  GbpRating,
  GbpReview,
  GbpReviewSnapshot,
  GbpTabId,
  GbpTone,
} from '../../growth-gbp/types';
import {
  GBP_KPIS,
  GBP_NEXT_POST,
  GBP_OFFER_SYNC,
  GBP_PHOTO_FRESHNESS,
  GBP_POSTS,
  GBP_PROFILE_ITEMS,
  GBP_QAS,
  GBP_REVIEW_SNAPSHOT,
  GBP_REVIEWS,
} from '../../growth-gbp/mockData';
import { GBP_TABS, GROWTH_GBP_META } from '../../growth-gbp/config';
import { Icon } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-gbp.css';

const TONE: Record<GbpTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  teal: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const RATING_VALUE: Record<GbpRating, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

function GbpCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function GbpStars({ rating }: { rating: GbpRating }) {
  const count = RATING_VALUE[rating];
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, index) => (
        <Icon
          key={index}
          kind="star"
          size={13}
          stroke={1.6}
          style={{ color: index < count ? 'var(--gold)' : 'var(--ink-4)' }}
        />
      ))}
    </span>
  );
}

function GbpKpiTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone: GbpTone;
}) {
  const accent = tone === 'neutral' ? 'var(--ink-0)' : TONE[tone].accent;
  return (
    <GbpCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{label}</div>
      <div
        className="mt-2 font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em]"
        style={{ color: accent }}
      >
        {value}
      </div>
      <div className="mt-2 text-[12px] text-[var(--ink-3)]">{sub}</div>
    </GbpCard>
  );
}

function GbpReviewRow({
  review,
  open,
  approved,
  onToggle,
  onApprove,
  onEdit,
  pending,
}: {
  review: GbpReview;
  open: boolean;
  approved: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onEdit?: () => void;
  pending?: boolean;
}) {
  return (
    <GbpCard
      className={[
        'mb-3 overflow-hidden',
        approved ? '!border-[color-mix(in_srgb,var(--success)_45%,transparent)]' : '',
        open && !approved ? '!border-[color-mix(in_srgb,var(--success)_45%,transparent)]' : '',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-4 border-0 bg-transparent px-5 py-4 text-left"
      >
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-semibold text-[var(--success)]"
          style={{ background: 'var(--success-tint)' }}
        >
          {review.reviewer.charAt(0)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-[var(--ink-0)]">
              {review.reviewer}
            </span>
            <GbpStars rating={review.rating} />
            <span className="text-[12px] text-[var(--ink-4)]">{review.date}</span>
          </div>
          <p
            className={[
              'mt-1 text-[13px] leading-[1.5] text-[var(--ink-2)]',
              open ? '' : 'truncate',
            ].join(' ')}
          >
            &ldquo;{review.text}&rdquo;
          </p>
        </div>
        {approved ? (
          <span
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[var(--r-pill)] px-3 py-1 text-[11px] font-bold"
            style={{ background: 'var(--success-tint)', color: 'var(--success)' }}
          >
            <Icon kind="check" size={12} stroke={2.4} />
            Approved
          </span>
        ) : (
          <span
            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-[var(--r-pill)] px-3 py-1 text-[11px] font-bold"
            style={{ background: TONE.terra.tint, color: 'var(--terracotta)' }}
          >
            Needs Approval
          </span>
        )}
        <Icon
          kind="down"
          size={16}
          stroke={2.2}
          className="shrink-0 text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {open ? (
        <div className="elva-fade border-t border-[var(--cream-2)] px-5 py-4">
          <div
            className="rounded-[var(--r-md)] border p-4"
            style={{
              borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)',
              background: 'var(--success-tint)',
            }}
          >
            <div className="mb-2 inline-flex items-center gap-2 text-[12px] font-semibold text-[var(--success)]">
              <Icon kind="sparkle" size={14} stroke={1.9} />
              ELVA&apos;s Drafted Response
            </div>
            <p className="text-[13px] leading-[1.7] text-[var(--ink-2)]">{review.draft}</p>
          </div>

          {approved ? (
            <div className="mt-3 text-[12px] font-medium text-[var(--success)]">
              Published to Google Business Profile just now
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onApprove}
                className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-5 py-2.5 text-[13px] font-semibold text-[var(--cream-1)]"
              >
                <Icon kind="check" size={14} stroke={2.4} />
                Approve &amp; Publish
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={onEdit}
                className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-2.5 text-[13px] font-medium text-[var(--ink-2)] disabled:cursor-wait disabled:opacity-60"
              >
                <Icon kind="pencil" size={14} stroke={1.9} />
                Edit Response
              </button>
              <button
                type="button"
                className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-2.5 text-[13px] font-medium text-[var(--ink-2)]"
              >
                Write My Own
              </button>
            </div>
          )}
        </div>
      ) : null}
    </GbpCard>
  );
}

function GbpStatusPill({ status, tone }: { status: string; tone: GbpTone }) {
  const t = TONE[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-0.5 text-[11px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
    >
      {tone === 'success' ? <span className="h-1.5 w-1.5 rounded-full" style={{ background: t.accent }} /> : null}
      {status}
    </span>
  );
}

function GbpPostRow({
  post,
  onApprove,
  pending,
  highlighted,
}: {
  post: GbpPost;
  onApprove?: () => void;
  pending?: boolean;
  highlighted?: boolean;
}) {
  const statusTone: GbpTone =
    post.status === 'Live' ? 'success' : post.status === 'Scheduled' ? 'teal' : 'neutral';
  const canApprove = post.status === 'Scheduled' && onApprove;
  return (
    <GbpCard
      className={[
        'mb-2.5 flex items-center gap-4 px-5 py-4',
        highlighted ? '!border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)]' : '',
      ].join(' ')}
    >
      <span
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--r-md)]"
        style={{ background: TONE[post.tone].tint, color: TONE[post.tone].accent }}
      >
        <Icon kind="doc" size={20} stroke={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span
            className="text-[10px] font-bold uppercase tracking-[0.06em]"
            style={{ color: TONE[post.tone].accent }}
          >
            {post.type}
          </span>
          <GbpStatusPill status={post.status} tone={statusTone} />
        </div>
        <div className="text-[14px] font-semibold text-[var(--ink-0)]">{post.title}</div>
      </div>
      <span className="shrink-0 text-[12px] text-[var(--ink-4)]">{post.date}</span>
      {canApprove ? (
        <button
          type="button"
          disabled={pending}
          onClick={onApprove}
          className="inline-flex shrink-0 cursor-pointer items-center rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-3.5 py-1.5 text-[12px] font-semibold text-[var(--cream-1)] disabled:cursor-wait disabled:opacity-60"
        >
          {pending ? 'Approving…' : 'Approve'}
        </button>
      ) : (
        <button
          type="button"
          className="inline-flex shrink-0 cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-1.5 text-[12px] font-medium text-[var(--ink-2)]"
        >
          View
        </button>
      )}
    </GbpCard>
  );
}

function GbpQaRow({
  qa,
  approved,
  onApprove,
}: {
  qa: GbpQa;
  approved: boolean;
  onApprove: () => void;
}) {
  const live = qa.status === 'Posted' || approved;
  return (
    <GbpCard
      className={[
        'mb-2.5 px-5 py-4',
        qa.status === 'Pending' && !approved
          ? '!border-[color-mix(in_srgb,var(--gold)_40%,transparent)]'
          : '',
      ].join(' ')}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        {live ? (
          <GbpStatusPill status="Live on Google" tone="success" />
        ) : (
          <span
            className="inline-flex items-center rounded-[var(--r-pill)] px-2.5 py-0.5 text-[11px] font-semibold"
            style={{ background: TONE.gold.tint, color: 'var(--gold)' }}
          >
            Pending Approval
          </span>
        )}
        {qa.category ? <GbpStatusPill status={qa.category} tone="neutral" /> : null}
      </div>
      <div className="mb-2 text-[14px] font-semibold text-[var(--ink-0)]">Q: {qa.q}</div>
      <div
        className="rounded-[var(--r-sm)] px-3.5 py-2.5 text-[13px] leading-[1.6] text-[var(--ink-2)]"
        style={{ background: 'var(--surface-mute)', borderLeft: '3px solid var(--success)' }}
      >
        <span className="font-semibold text-[var(--success)]">A: </span>
        {qa.a}
      </div>
      {qa.status === 'Pending' && !approved ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onApprove}
            className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2 text-[12px] font-semibold text-[var(--cream-1)]"
          >
            Approve &amp; Post
          </button>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[12px] font-medium text-[var(--ink-2)]"
          >
            Edit Answer
          </button>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[12px] font-medium text-[var(--ink-2)]"
          >
            Dismiss
          </button>
        </div>
      ) : null}
    </GbpCard>
  );
}

function GbpProfilePriorityPill({ priority }: { priority: GbpProfileItem['priority'] }) {
  const tone: GbpTone =
    priority === 'Critical' ? 'terra' : priority === 'High' ? 'gold' : 'neutral';
  const t = TONE[tone];
  return (
    <span
      className="inline-flex items-center rounded-[var(--r-pill)] px-2 py-0.5 text-[10px] font-bold"
      style={{ background: t.tint, color: t.accent }}
    >
      {priority}
    </span>
  );
}

function ReviewsTab({
  reviews,
  openIndex,
  onToggle,
  onApprove,
  onEditReview,
  reviewsDegraded,
  pendingReviewId,
}: {
  reviews: GbpReview[];
  openIndex: number | null;
  onToggle: (index: number) => void;
  onApprove: (id: number) => void;
  onEditReview?: (id: number) => void;
  reviewsDegraded?: boolean;
  pendingReviewId?: number;
}) {
  return (
    <div>
      {reviewsDegraded ? (
        <div
          className="mb-5 flex flex-wrap items-center gap-3 rounded-[var(--r-xl)] border px-4 py-3"
          style={{
            borderColor: 'color-mix(in srgb, var(--terracotta) 26%, transparent)',
            background: 'color-mix(in srgb, var(--terracotta) 8%, transparent)',
          }}
        >
          <Icon kind="alert" size={16} stroke={1.9} className="text-[var(--terracotta)]" />
          <p className="text-[13px] leading-[1.5] text-[var(--ink-2)]">
            Review sync is temporarily unavailable. Showing cached data or an empty list until the
            pipeline reconnects.
          </p>
        </div>
      ) : null}
      <div
        className="mb-5 flex flex-wrap items-center gap-3 rounded-[var(--r-xl)] border px-4 py-3"
        style={{
          borderColor: 'color-mix(in srgb, var(--gold) 26%, transparent)',
          background: 'var(--gold-tint)',
        }}
      >
        <Icon kind="lock" size={16} stroke={1.9} className="text-[var(--gold)]" />
        <p className="text-[13px] leading-[1.5] text-[var(--gold)]">
          <span className="font-semibold">
            All review responses require your approval before publishing.
          </span>{' '}
          <span className="text-[var(--ink-3)]">
            ELVA drafts personalized responses, but you have the final say. Edit, approve, or write
            your own.
          </span>
        </p>
      </div>

      {reviews.length === 0 ? (
        <GbpCard className="px-5 py-8 text-center text-[13px] text-[var(--ink-3)]">
          No reviews loaded yet.
        </GbpCard>
      ) : null}

      {reviews.map((review, index) => (
        <GbpReviewRow
          key={`${review.id}-${index}`}
          review={review}
          open={openIndex === index}
          approved={review.status === 'approved'}
          onToggle={() => {
            onToggle(index);
          }}
          onApprove={() => {
            onApprove(review.id);
          }}
          {...(onEditReview ? { onEdit: () => onEditReview(review.id) } : {})}
          {...(pendingReviewId === review.id ? { pending: true } : {})}
        />
      ))}
    </div>
  );
}

function PostsTab({
  posts,
  nextPost,
  useMockNext = false,
  onApprovePost,
  pendingPostId,
  focusPostId,
}: {
  posts: GbpPost[];
  nextPost?: typeof GBP_NEXT_POST;
  useMockNext?: boolean;
  onApprovePost?: (id: number) => void;
  pendingPostId?: number;
  focusPostId?: number;
}) {
  const focusRef = useRef<HTMLDivElement>(null);
  const scheduled = nextPost ?? (useMockNext ? GBP_NEXT_POST : undefined);

  useEffect(() => {
    if (focusPostId == null) return;
    focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusPostId, posts]);

  return (
    <div>
      <p className="mb-4 text-[13px] text-[var(--ink-3)]">
        ELVA publishes one post per week automatically. You can also create manual posts.
      </p>
      {posts.length === 0 ? (
        <GbpCard className="mb-4 px-5 py-8 text-center text-[13px] text-[var(--ink-3)]">
          No posts yet.
        </GbpCard>
      ) : (
        posts.map((post) => (
          <div key={post.id} ref={focusPostId === post.id ? focusRef : undefined}>
            <GbpPostRow
              post={post}
              highlighted={focusPostId === post.id}
              {...(onApprovePost ? { onApprove: () => onApprovePost(post.id) } : {})}
              {...(pendingPostId === post.id ? { pending: true } : {})}
            />
          </div>
        ))
      )}

      {scheduled ? (
        <div
          className="mt-5 rounded-[var(--r-xl)] border px-5 py-5"
          style={{
            borderColor: 'color-mix(in srgb, var(--success) 26%, transparent)',
            background: 'var(--success-tint)',
          }}
        >
          <div className="mb-2 inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--success)]">
            <Icon kind="sparkle" size={15} stroke={1.9} />
            {scheduled.title}
          </div>
          <p className="mb-3 text-[13px] leading-[1.6] text-[var(--ink-2)]">{scheduled.body}</p>
          <p className="text-[12px] text-[var(--ink-3)]">{scheduled.footnote}</p>
        </div>
      ) : null}
    </div>
  );
}

function QaTab({
  qas,
  approvedIds,
  onApprove,
}: {
  qas: GbpQa[];
  approvedIds: Set<number>;
  onApprove: (id: number) => void;
}) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[640px] text-[13px] text-[var(--ink-3)]">
          Questions seeded by ELVA and answered from your practice profile. More Q&amp;As = better Map
          Pack ranking.
        </p>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]"
        >
          <Icon kind="plus" size={14} stroke={2.2} />
          Suggest New Q&amp;A
        </button>
      </div>
      {qas.map((qa) => (
        <GbpQaRow
          key={qa.id}
          qa={qa}
          approved={approvedIds.has(qa.id)}
          onApprove={() => {
            onApprove(qa.id);
          }}
        />
      ))}
    </div>
  );
}

function ProfileHealthTab({
  profileItems,
  profileCompletenessPct,
  photoFreshness,
  reviewSnapshot,
  offerSync,
}: {
  profileItems?: GbpProfileItem[];
  profileCompletenessPct?: number;
  photoFreshness?: GbpPhotoFreshness;
  reviewSnapshot?: GbpReviewSnapshot;
  offerSync?: typeof GBP_OFFER_SYNC;
}) {
  const items = profileItems ?? GBP_PROFILE_ITEMS;
  const completePct =
    profileCompletenessPct ??
    Math.round((items.filter((item) => item.complete).length / items.length) * 100);
  const missing = items.filter((item) => !item.complete);
  const ringTone: GbpTone = completePct >= 80 ? 'success' : 'gold';
  const fresh = photoFreshness ?? GBP_PHOTO_FRESHNESS;
  const snapshot = reviewSnapshot ?? GBP_REVIEW_SNAPSHOT;
  const offer = offerSync ?? GBP_OFFER_SYNC;
  const r = 26;
  const circ = 2 * Math.PI * r;

  return (
    <div className="gbp-profile-grid grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="flex flex-col gap-5">
        <GbpCard className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                Profile Completeness
              </div>
              <div className="mt-0.5 text-[12px] text-[var(--ink-4)]">
                Complete profiles rank higher in the Map Pack
              </div>
            </div>
            <div className="relative h-14 w-14 shrink-0">
              <svg width="56" height="56" className="-rotate-90">
                <circle cx="28" cy="28" r={r} fill="none" stroke="var(--surface-mute)" strokeWidth="4" />
                <circle
                  cx="28"
                  cy="28"
                  r={r}
                  fill="none"
                  stroke={TONE[ringTone].accent}
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={circ}
                  strokeDashoffset={circ * (1 - completePct / 100)}
                />
              </svg>
              <div
                className="absolute inset-0 flex items-center justify-center text-[15px] font-bold"
                style={{ color: TONE[ringTone].accent }}
              >
                {completePct}%
              </div>
            </div>
          </div>

          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--terracotta)]">
            Missing ({missing.length} items)
          </div>
          <div className="flex flex-col gap-1.5">
            {missing.map((item) => (
              <div
                key={item.field}
                className="flex items-center gap-2.5 rounded-[var(--r-sm)] px-3 py-2"
                style={{ background: TONE.terra.tint }}
              >
                <Icon kind="x" size={13} stroke={2.4} className="shrink-0 text-[var(--terracotta)]" />
                <span className="flex-1 text-[13px] font-medium text-[var(--ink-1)]">{item.field}</span>
                <GbpProfilePriorityPill priority={item.priority} />
              </div>
            ))}
          </div>

          <button
            type="button"
            className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-[var(--r-sm)] border px-4 py-2.5 text-[13px] font-semibold"
            style={{
              borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)',
              background: 'var(--success-tint)',
              color: 'var(--success)',
            }}
          >
            <Icon kind="activity" size={15} stroke={1.9} />
            See Full Benchmark vs Competitors
          </button>
          <div className="mt-1.5 text-center text-[11px] text-[var(--ink-4)]">
            Compare your profile against Smile Central and 7 other competitors on the Map Pack
            Intelligence page
          </div>
        </GbpCard>

        <GbpCard className="px-5 py-5">
          <div className="mb-2.5 flex items-center gap-2">
            <Icon kind="refresh" size={16} stroke={1.9} className="text-[var(--ink-2)]" />
            <span className="text-[14px] font-semibold text-[var(--ink-0)]">Offer Sync Status</span>
          </div>
          <div
            className="flex items-center gap-2.5 rounded-[var(--r-sm)] px-3.5 py-2.5"
            style={{ background: 'var(--success-tint)' }}
          >
            <GbpStatusPill status={offer.status} tone="success" />
            <span className="text-[13px] text-[var(--ink-2)]">
              <strong className="font-semibold">{offer.offer}</strong> {offer.body}
            </span>
          </div>
          <div className="mt-2 text-[12px] text-[var(--ink-4)]">{offer.footnote}</div>
        </GbpCard>
      </div>

      <div className="flex flex-col gap-5">
        <GbpCard className="border-[color-mix(in_srgb,var(--gold)_30%,transparent)] p-6">
          <div className="mb-3.5 flex items-center gap-2">
            <Icon kind="image" size={18} stroke={1.9} className="text-[var(--gold)]" />
            <div className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              Photo Freshness
            </div>
          </div>
          <div className="mb-4 flex items-center gap-4">
            <div>
              <div className="font-[var(--font-display)] text-[36px] font-extrabold leading-none tracking-[-0.04em] text-[var(--gold)]">
                {fresh.daysSinceUpload}
              </div>
              <div className="text-[12px] text-[var(--ink-4)]">days since last upload</div>
            </div>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--surface-mute)]">
              <div
                className="h-full rounded-full bg-[var(--gold)]"
                style={{ width: `${fresh.progressPct}%` }}
              />
            </div>
            <div className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--gold)]">
              <Icon kind="alert" size={12} stroke={2} />
              Due for update
            </div>
          </div>
          <div
            className="rounded-[var(--r-sm)] px-4 py-3 text-[13px] leading-[1.6] text-[var(--ink-2)]"
            style={{ background: 'var(--gold-tint)' }}
          >
            {fresh.body}
          </div>
          <div className="mt-3 flex items-center gap-2 text-[12px] text-[var(--ink-4)]">
            <span>
              <strong className="font-semibold">Total photos:</strong> {fresh.totalPhotos}
            </span>
            {fresh.competitorAvg > 0 ? (
              <>
                <span>|</span>
                <span>
                  <strong className="font-semibold">Competitor avg:</strong> {fresh.competitorAvg}
                </span>
              </>
            ) : null}
          </div>
        </GbpCard>

        <GbpCard className="p-6">
          <div className="mb-3.5 flex items-center gap-2">
            <Icon kind="star" size={18} stroke={1.9} className="text-[var(--gold)]" />
            <div className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              Review Snapshot
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {snapshot.stats.map((stat) => (
              <div
                key={stat.key}
                className="rounded-[var(--r-sm)] px-3 py-3 text-center"
                style={{
                  background: stat.tone === 'success' ? 'var(--success-tint)' : 'var(--surface-mute)',
                }}
              >
                <div
                  className="font-[var(--font-display)] text-[24px] font-extrabold leading-none tracking-[-0.04em]"
                  style={{ color: stat.tone === 'neutral' ? 'var(--ink-0)' : TONE[stat.tone].accent }}
                >
                  {stat.value}
                </div>
                <div className="mt-1 text-[11px] text-[var(--ink-4)]">{stat.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-3.5 text-[13px] leading-[1.6] text-[var(--ink-3)]">{snapshot.body}</p>
        </GbpCard>
      </div>
    </div>
  );
}

export interface GbpManagerViewProps {
  reviews?: GbpReview[];
  posts?: GbpPost[];
  qas?: GbpQa[];
  kpis?: typeof GBP_KPIS;
  profileItems?: GbpProfileItem[];
  profileCompletenessPct?: number;
  photoFreshness?: GbpPhotoFreshness;
  reviewSnapshot?: GbpReviewSnapshot;
  offerSync?: GbpOfferSync;
  nextPost?: GbpNextPost;
  reviewsDegraded?: boolean;
  loading?: boolean;
  onApproveReview?: (id: number) => void;
  onEditReview?: (id: number) => void;
  onApproveQa?: (id: number) => void;
  onApprovePost?: (id: number) => void;
  onCreatePost?: () => void;
  onViewOnGoogle?: () => void;
  pendingReviewId?: number;
  pendingPostId?: number;
  initialTab?: GbpTabId;
  focusPostId?: number;
  initialOpenReviewId?: number;
}

export function GbpManagerView({
  reviews: reviewsProp,
  posts: postsProp,
  qas: qasProp,
  kpis: kpisProp,
  profileItems: profileItemsProp,
  profileCompletenessPct,
  photoFreshness: photoFreshnessProp,
  reviewSnapshot: reviewSnapshotProp,
  offerSync,
  nextPost,
  reviewsDegraded = false,
  loading = false,
  onApproveReview,
  onEditReview,
  onApproveQa,
  onApprovePost,
  onCreatePost,
  onViewOnGoogle,
  pendingReviewId,
  pendingPostId,
  initialTab,
  focusPostId,
  initialOpenReviewId,
}: GbpManagerViewProps = {}) {
  const meta = GROWTH_GBP_META;
  const controlled = reviewsProp !== undefined;
  const sourceReviews = reviewsProp ?? GBP_REVIEWS;
  const sourcePosts = postsProp ?? GBP_POSTS;
  const sourceQas = qasProp ?? GBP_QAS;
  const kpis = kpisProp?.length ? kpisProp : GBP_KPIS;
  const profileHealthControlled = profileItemsProp !== undefined;
  const [activeTab, setActiveTab] = useState<GbpTabId>(initialTab ?? 'reviews');
  const [openReviewIndex, setOpenReviewIndex] = useState<number | null>(() => {
    if (initialOpenReviewId == null) return null;
    const idx = (reviewsProp ?? GBP_REVIEWS).findIndex((review) => review.id === initialOpenReviewId);
    return idx >= 0 ? idx : null;
  });
  const [approvedReviews, setApprovedReviews] = useState<Set<number>>(() => new Set());
  const [approvedQas, setApprovedQas] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (initialOpenReviewId == null) return;
    const idx = sourceReviews.findIndex((review) => review.id === initialOpenReviewId);
    if (idx >= 0) {
      setActiveTab('reviews');
      setOpenReviewIndex(idx);
    }
  }, [initialOpenReviewId, sourceReviews]);

  const reviews: GbpReview[] = controlled
    ? sourceReviews
    : sourceReviews.map((review) =>
        approvedReviews.has(review.id) ? { ...review, status: 'approved' } : review,
      );

  const tabOptions = GBP_TABS.map((tab) => ({ value: tab.id, label: tab.label }));

  return (
    <div className="growth-gbp-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="pin" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
        >
          <Icon kind="activity" size={15} stroke={1.9} className="text-[var(--ai-3)]" />
          Competitive Benchmark
          <Icon kind="external" size={13} stroke={1.9} className="text-[var(--ink-4)]" />
        </button>
        <button
          type="button"
          onClick={onViewOnGoogle}
          className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
        >
          View on Google
          <Icon kind="external" size={13} stroke={1.9} className="text-[var(--ink-4)]" />
        </button>
        <button
          type="button"
          onClick={onCreatePost}
          className="ml-auto inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]"
        >
          <Icon kind="plus" size={14} stroke={2.2} />
          Create Post
        </button>
      </div>

      <div
        className="gbp-kpi-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
      >
        {kpis.map((kpi) => (
          <GbpKpiTile
            key={kpi.key}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.sub}
            tone={kpi.tone}
          />
        ))}
      </div>

      <div className="mb-5">
        <Segmented
          options={tabOptions}
          value={activeTab}
          onChange={(value) => {
            setActiveTab(value as GbpTabId);
          }}
        />
      </div>

      {activeTab === 'reviews' ? (
        <ReviewsTab
          reviews={reviews}
          openIndex={openReviewIndex}
          onToggle={(index) => {
            setOpenReviewIndex((current) => (current === index ? null : index));
          }}
          onApprove={(id) => {
            if (onApproveReview) {
              onApproveReview(id);
              return;
            }
            setApprovedReviews((current) => new Set(current).add(id));
          }}
          {...(onEditReview ? { onEditReview } : {})}
          {...(reviewsDegraded ? { reviewsDegraded: true } : {})}
          {...(pendingReviewId != null ? { pendingReviewId } : {})}
        />
      ) : null}

      {activeTab === 'posts' ? (
        <PostsTab
          posts={sourcePosts}
          useMockNext={postsProp === undefined}
          {...(nextPost ? { nextPost } : {})}
          {...(onApprovePost ? { onApprovePost } : {})}
          {...(pendingPostId != null ? { pendingPostId } : {})}
          {...(focusPostId != null ? { focusPostId } : {})}
        />
      ) : null}

      {activeTab === 'qa' ? (
        <QaTab
          qas={sourceQas}
          approvedIds={approvedQas}
          onApprove={(id) => {
            if (onApproveQa) {
              onApproveQa(id);
              return;
            }
            setApprovedQas((current) => new Set(current).add(id));
          }}
        />
      ) : null}

      {activeTab === 'profile' ? (
        <ProfileHealthTab
          {...(profileHealthControlled
            ? {
                profileItems: profileItemsProp,
                profileCompletenessPct,
                photoFreshness: photoFreshnessProp,
                reviewSnapshot: reviewSnapshotProp,
                ...(offerSync ? { offerSync } : {}),
              }
            : {})}
        />
      ) : null}
    </div>
  );
}
